import { useEffect } from 'react';
import type { MutableRefObject } from 'react';
import type { LiveAvatarSession } from '@heygen/liveavatar-web-sdk';
import { SessionState } from '@heygen/liveavatar-web-sdk';

/** How often to ping the HeyGen keep-alive endpoint while the user is active. */
const KEEP_ALIVE_INTERVAL_MS = 30_000; // 30 seconds

/** How long to wait without user interaction before stopping the keep-alive pings. */
const INACTIVITY_TIMEOUT_MS = 5 * 60 * 1_000; // 5 minutes

/** User interaction events that count as "activity" and reset the inactivity timer. */
const ACTIVITY_EVENTS = ['click', 'keydown', 'touchstart'] as const;

/**
 * Keeps a HeyGen LiveAvatar session alive while the user is active.
 *
 * Behaviour:
 * - Sends a keep-alive ping via `session.keepAlive()` every 30 seconds while
 *   the session is CONNECTED and the user has been active within the last 5 minutes.
 * - Any `click`, `keydown`, or `touchstart` on `window` resets the 5-minute
 *   inactivity timer.
 * - Once 5 minutes of inactivity elapse, pinging stops. HeyGen then expires the
 *   session on its own idle-timeout schedule (also 5 minutes from the last ping).
 * - All timers and event listeners are cleaned up on unmount or when the session
 *   state changes away from CONNECTED (e.g. on disconnect or reconnect).
 *
 * This hook is intentionally side-effect-only — it returns nothing and owns no
 * state. Call it inside the component that owns `sessionRef` and `sessionState`.
 *
 * @param sessionRef - Stable ref to the `LiveAvatarSession` instance.
 * @param sessionState - Reactive session state (from `LiveAvatarContext`).
 */
export function useSessionKeepAlive(
  sessionRef: MutableRefObject<LiveAvatarSession | null>,
  sessionState: SessionState,
): void {
  useEffect(() => {
    // Only run while the session is fully established — not during CONNECTING,
    // DISCONNECTING, or INACTIVE states.
    if (sessionState !== SessionState.CONNECTED) return;

    // `active` tracks whether the user has interacted within the last 5 minutes.
    // It starts true so a freshly connected session immediately begins pinging.
    let active = true;

    // Use `let` so `resetInactivityTimer` can clear and reassign the timer ID.
    // Initialized to `undefined` so the first `clearTimeout` call is a safe no-op.
    let inactivityTimer: ReturnType<typeof setTimeout> | undefined = undefined;

    const resetInactivityTimer = () => {
      active = true;
      clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(() => {
        // 5 minutes have elapsed with no interaction. Stop pinging and let HeyGen
        // expire the session on its own schedule.
        active = false;
      }, INACTIVITY_TIMEOUT_MS);
    };

    // Start the inactivity timer immediately so a session that is opened but
    // never interacted with still expires after 5 minutes.
    resetInactivityTimer();

    // Reset the timer on any user interaction.
    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, resetInactivityTimer);
    }

    // Ping every 30 seconds, but only while the user is still active.
    const intervalId = setInterval(() => {
      if (!active) return;

      // sessionRef.current may be null if the session was stopped while the
      // interval was already scheduled — safe to no-op in that case.
      sessionRef.current?.keepAlive().catch(() => {
        // Ignore keep-alive errors — the session may have already ended.
        // HeyGen will close the session on its side; our component will receive
        // a SESSION_STATE_CHANGED event and unmount this effect via cleanup.
      });
    }, KEEP_ALIVE_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
      clearTimeout(inactivityTimer);
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, resetInactivityTimer);
      }
    };
  }, [sessionState, sessionRef]);
}
